import React from "react";
import { ChildLabelSelection } from "./ChildLabelSelection";
import type { FieldComponentProps } from "../fieldRegistry";
import type { Label } from "@/types/label";

// Extend FieldConfig to include label-specific properties
interface ChildLabelsFieldConfig {
  labels?: Label[];
  onManageLabels?: () => void;
  entityTypeFilter?: string;
  newlyCreatedLabelId?: string | null;
}

interface ChildLabelsFieldProps extends FieldComponentProps<string[]> {
  labels?: Label[];
  onManageLabels?: () => void;
}

export function ChildLabelsField({
  value,
  onChange,
  field,
}: ChildLabelsFieldProps) {
  // Extract labels and onManageLabels from field config if available
  const fieldWithLabels = field as typeof field & ChildLabelsFieldConfig;
  const labels = fieldWithLabels.labels || [];
  const onManageLabels = fieldWithLabels.onManageLabels;
  const entityTypeFilter = fieldWithLabels.entityTypeFilter || "children";
  const newlyCreatedLabelId = fieldWithLabels.newlyCreatedLabelId;

  // Auto-select a newly created label
  const processedLabelRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (newlyCreatedLabelId && newlyCreatedLabelId !== processedLabelRef.current) {
      processedLabelRef.current = newlyCreatedLabelId;
      const currentLabels = value || [];
      if (!currentLabels.includes(newlyCreatedLabelId)) {
        onChange([...currentLabels, newlyCreatedLabelId]);
      }
    }
  }, [newlyCreatedLabelId, value, onChange]);

  // Filter labels to show "all" + the specific entity type for this context
  const filteredLabels = labels.filter(
    (label: Label) => label.entity_type === entityTypeFilter || label.entity_type === "all"
  );

  return (
    <ChildLabelSelection
      selectedLabels={value || []}
      onLabelsChange={onChange}
      labels={filteredLabels}
      onManageLabels={onManageLabels}
    />
  );
}
